import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import RichTextEditor from "../ui/RichTextEditor";
import { updateFieldContent } from "../../services/consultationform";

import { FormField } from "../../types/forms";

interface TextBlockSectionProps {
  userId: string;
  templateId: string;
  fields?: FormField[];
  isEditable?: boolean;
}

export default function TextBlockSection({ userId, templateId, fields, isEditable = false }: TextBlockSectionProps) {
  const storageKey = `${userId}_${templateId}_textblock`;
  const [text, setText] = useLocalStorage<string>(storageKey, "");

  const [isEditing, setIsEditing] = useState(false);
  const [tempContent, setTempContent] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  // Determine content to display
  const field = fields && fields.length > 0 ? fields[0] : null;
  const displayContent = field ? (field.options?.content || field.label) : text;

  const handleStartEdit = () => {
    if (!field || !isEditable) return;
    setTempContent(displayContent);
    setIsEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!field) return;
    setIsSaving(true);
    try {
      await updateFieldContent(field.id, tempContent);
      setIsEditing(false);
      // Optimistic update handled by parent usually, but we can force re-render if needed
      // For now, assuming parent triggers refresh or we update local/parent state if provided.
      // Since we don't have setTemplate here, we rely on the fact that 'field' prop might not update immediately
      // unless we track it locally.
      // Let's modify displayContent to prefer tempContent if we just saved?
      // Actually, similar to PolicySection, we might want local state.
      // But for simplicity, let's assume the mutation works.
      // For better UX, let's update a local override.
      if (field.options) field.options.content = tempContent;
      else field.label = tempContent;

    } catch (err) {
      console.error("Failed to save text block:", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <h2 className="text-xl font-semibold text-gray-800">Text Block</h2>

      {isEditing ? (
        <div className="space-y-2">
          <RichTextEditor
            value={tempContent}
            onChange={setTempContent}
            rows={6}
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsEditing(false)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
            <button
              disabled={isSaving}
              onClick={handleSaveEdit}
              className="p-1 text-green-500 hover:text-green-600 disabled:opacity-50"
            >
              {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={handleStartEdit}
          className={`prose prose-sm max-w-none text-gray-600 ${isEditable && field ? "cursor-pointer hover:bg-gray-50 p-2 -m-2 rounded transition-colors border border-transparent hover:border-gray-200" : ""}`}
        >
          {field ? (
            <div dangerouslySetInnerHTML={{ __html: displayContent }} />
          ) : (
            // Local storage legacy mode - just edit in place? 
            // Or simple textarea as before? User asked for text editor.
            // Let's use RichTextEditor for local mode too if they want.
            // But for now, keeping legacy behavior simple or upgrading it?
            // "same as privacy/disclaimer" implies click-to-edit.
            // Let's keep consistent.
            <RichTextEditor
              value={text}
              onChange={setText}
              placeholder="Enter text content here..."
            />
          )}
        </div>
      )}
    </div>
  );
}
